<template>
  <div
    class="plyr-player-container"
    @keydown="keyPressed"
    tabindex="0"
    role="region"
    :aria-label="$t('routes.pod.videoPlayer')"
  >
    <vue-plyr v-if="canPlaySource()" :key="pod?.id" ref="plyr" v-bind:options="options" class="d-flex justify-center" style="">
      <div class="plyr-player-wrapper">
        <video
          :aria-label="$t('routes.pod.videoPlayer')"
          crossorigin
          playsinline
          preload="auto"
          ref="plyrVideoSource"
          :data-player-pod-id="pod?.id"
          :x-webkit-airplay="!castLocked && (airPlayActive || airPlayEligibility.allowed) ? 'allow' : 'deny'"
          :disableRemotePlayback="castLocked || (!airPlayActive && !airPlayEligibility.allowed)"
          @webkitplaybacktargetavailabilitychanged="onAirPlayAvailabilityChanged"
          @webkitcurrentplaybacktargetiswirelesschanged="onAirPlayWirelessChanged"
          :src="getSource?.url ?? ''"
          v-on:ready="plyReady"
          v-on:loadeddata="plyrLoadedData"
          v-on:play="plyrPlay"
          v-on:pause="plyrPause"
          v-on:timeupdate="plyrTimeUpdate"
          v-on:volumechange="plyrVolumeChanged"
          v-on:seeking="plyrSeeking"
          v-on:seeked="plyrSeeked"
          v-on:ratechange="plyrSpeedChanged"
          v-on:error="handlePlayerError"
          v-bind:data-poster="getPoster"
          v-bind:style="getVideoTagStyle"
        >
          <track
            v-if="activeSubtitle && !castOwned"
            kind="subtitles"
            v-bind:label="activeSubtitle.label"
            v-bind:src="activeSubtitle.url"
            v-bind:srclang="activeSubtitle.language"
          />
        </video>

        <AWSTextLayer v-if="!castLocked && getActiveLayer === 2" data-cy="player-layer-text" />
        <AWSLabelsLayer v-if="!castLocked && getActiveLayer === 3" data-cy="player-layer-labels" />
      </div>
    </vue-plyr>

    <div v-else class="p-5 text-orange">
      <v-icon class="text-orange">mdi mdi-alert-circle-outline</v-icon>
      {{ $t('routes.pod.originalFileNotCompatible') }}
      {{ !hasPreviewFile ? $t('routes.pod.waitUntilTranscodeCompatible') : '' }}
    </div>

    <audio
      v-if="canPlaySource() && activeAudioDescription"
      ref="audioDescriptionSource"
      :src="activeAudioDescription.url"
      crossorigin
      preload="auto"
      aria-hidden="true"
      class="d-none"
    />

    <PlayerControls
      v-if="canPlaySource()"
      v-bind:transcodedAsset="transcodedAsset"
      v-bind:pod="pod"
      v-bind:enabledWaveform="hasWaveform"
      v-bind:showQualitySelection="showQualitySelection"
      v-bind:qualityOptions="qualityOptions"
      v-bind:activeQuality="activeQuality"
      :comments="comments"
      :air-play-visible="airPlayVisible"
      :air-play-active="airPlayActive"
      :air-play-disabled="airPlayDisabled"
      :air-play-disabled-reason="airPlayDisabledReason"
      :air-play-description-id="airPlayDescriptionId"
      :air-play-error-key="airPlayErrorKey"
      :playback-commands="playbackCommands"
      :cast-ui="castUi"
      @cast="startCasting"
      @stop-cast="stopCasting"
      @resume-local="resumeLocalPlayback"
      @cast-caption-change="changeCastCaption"
      @airplay="openAirPlayPicker"
      v-on:qualityChange="setQuality"
      v-on:waveformToggle="onWaveformToggle"
    />

    <div v-if="canPlaySource() && hasWaveform && waveFormHasLoaded" v-show="waveformToggle && !castLocked" class="text-center my-2" style="overflow: auto;">
      <canvas ref="waveformCanvas" width="400" height="80" />
    </div>

    <v-select
      v-if="allowSourceSelection"
      v-model="activeSource"
      :items="sourceOptions"
      :disabled="airPlayActive || castLocked"
      item-title="text"
      item-value="value"
      :label="$t('routes.pod.fileSource')"
      :aria-label="$t('routes.pod.fileSource')"
      :menu-props="{
        activatorProps: {
          'aria-label': $t('routes.pod.fileSource')
        },
      }"
      :item-props="() => ({ tabindex: 0 })"
      class="pt-2"
      variant="solo-filled"
      density="comfortable"
      single-line
      flat
      hide-details
      eager
    />

  </div>
</template>

<script>
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import PlayerControls from './PlayerControls.vue';
import AWSLabelsLayer from './AWSLabelsLayer.vue';
import AWSTextLayer from './AWSTextLayer.vue';
import { useAVWaveform } from 'vue-audio-visual';
import { toRef, markRaw } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { getAirPlayEligibility } from './airplay-policy.js';
import { getCastEligibility } from './cast-policy.js';
import { getGoogleCastSession } from './google-cast.js';
import { event as gtagEvent } from 'vue-gtag';

export default {
  name: 'PlyrPlayer',
  components: {
    PlayerControls,
    AWSLabelsLayer,
    AWSTextLayer,
  },
  props: {
    pod: Object,
    accessExpiresAt: {
      type: String,
      default: null,
    },
    useRawFile: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowSourceSelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowQualitySelection: {
      type: Boolean,
      required: false,
      default: false,
    },
    comments: {
      type: Array,
      required: false,
    },
    // Controls whether player should persist its settings in the local storage.
    persistSettings: {
      type: Boolean,
      required: false,
    },
    viewContext: {
      type: String,
      required: false,
      default: 'file',
    },
  },
  data: function () {
    return {
      waveformToggle: false,
      waveFormHasLoaded: false,   // prevents loading the waveform on page load
      activeSource: null,
      activeQuality: 'auto',
      networkInformation: null,
      canActuallyPlayRawFile: true,
      pendingPlaybackState: null,
      sourcePodId: this.pod?.id,
      airPlaySupported: false,
      airPlayAvailable: false,
      airPlayActive: false,
      airPlayErrorKey: null,
      airPlayNowMs: Date.now(),
      airPlayClockTimer: null,
      airPlayPinnedSource: null,
      airPlayPinnedPodId: null,
      airPlaySessionId: null,
      airPlayDescriptionId: `airplay-description-${uuidv4()}`,
      airPlayMediaDuration: null,
      airPlayCurrentTime: 0,
      castService: null,
      castUnsubscribe: null,
      castSnapshot: null,
      castLifecycleToken: 0,
      castDisposed: false,
      castPending: false,
      castReturning: false,
      castStopRequested: false,
      castReturnRetryState: null,
      castOwnedPodId: null,
      castOwnedAssetId: null,
      castPinnedSource: null,
      castPinnedPodId: null,
      castLockedSourceMode: null,
      castLockedQuality: null,
      castLoadedCaption: null,
      castLocalSubtitle: null,
      castLocalAudioDescription: null,
      castAccessibilityBlocked: false,
      castUnsupportedReceiverCaption: false,
      castReturnState: null,
      castResumeLocal: false,
      castErrorKey: null,
      castDescriptionId: `cast-description-${uuidv4()}`,
    };
  },
  mounted: function () {
    const playerStore = usePlayerStore();
    playerStore.resetPlayer();
    playerStore.refreshSubtitles(this.pod);

    // Set the default source to original if the user is allowed to select.
    if (this.viewContext === 'accesslink') {
      if (this.allowSourceSelection && this.canActuallyPlayRawFile) {
        this.activeSource = 'original';
      } else {
        this.activeSource = 'preview';
      }
    } else {
      this.activeSource = 'preview';
    }
    this.networkInformation = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
    window.addEventListener('resize', this.refreshAutoQuality);
    this.networkInformation?.addEventListener('change', this.refreshAutoQuality);
    this.refreshAutoQuality();
    this.$nextTick(this.syncAirPlayState);
    this.initializeCast();
  },
  beforeUnmount: function () {
    window.removeEventListener('resize', this.refreshAutoQuality);
    this.networkInformation?.removeEventListener('change', this.refreshAutoQuality);
    clearInterval(this.airPlayClockTimer);
    this.castDisposed = true;
    this.invalidateCastContext();
    this.castUnsubscribe?.();
  },
  watch: {
    'pod.id': function () {
      this.invalidateCastContext();
      // Navigation is not a native disconnect; never emit a synthetic stop.
      this.airPlayActive = false;
      this.airPlayAvailable = false;
      this.airPlaySupported = false;
      this.airPlayPinnedSource = null;
      this.airPlayPinnedPodId = null;
      this.airPlaySessionId = null;
      this.airPlayMediaDuration = null;
      this.airPlayCurrentTime = 0;
      this.airPlayErrorKey = null;
      this.pendingPlaybackState = null;
      this.refreshAirPlayClock();
      this.$nextTick(this.syncAirPlayState);
    },
    accessExpiresAt: 'refreshAirPlayClock',
    pod: function (newPod) {
      const playerStore = usePlayerStore();
      playerStore.refreshSubtitles(newPod);
      this.$nextTick(() => this.reconcileCastSession());
    },
    audioDescriptionAssets: {
      handler: function () {
        const playerStore = usePlayerStore();
        playerStore.refreshAudioDescriptions(this.pod);
      },
      deep: true,
      immediate: true,
    },
    getSource: function (newSource, oldSource) {
      const samePod = this.sourcePodId === this.pod?.id;
      this.sourcePodId = this.pod?.id;
      this.airPlayErrorKey = null;
      this.airPlayMediaDuration = null;
      this.refreshAirPlayClock();
      if (newSource === oldSource) return;
      const returning = this.pendingPlaybackState?.castReturn ? this.pendingPlaybackState : this.castReturnRetryState;
      if (samePod && returning && newSource?.id === returning.sourceId) {
        if (this.getAuthorizedCastReturnSource({ id: returning.sourceId, url: returning.sourceUrl })) {
          if (returning.sourceUrl !== newSource.url) returning.finishing = false;
          returning.sourceUrl = newSource.url;
          return;
        }
        if (this.castReturning) {
          this.failCastLocalReturn();
          return;
        }
      }
      if (samePod && returning?.castReturn && this.castReturning && newSource?.id !== returning.sourceId) {
        this.failCastLocalReturn();
        return;
      }
      if (!this.castLocked && this.castReturnRetryState) {
        this.castReturnRetryState = null;
        this.castResumeLocal = false;
      }
      if (this.castLocked) return;
      if (samePod && newSource?.id !== oldSource?.id && oldSource && newSource) {
        const player = this.plyr();
        this.pendingPlaybackState = {
          currentTime: Number(player?.currentTime) || 0,
          sourceId: newSource.id,
          wasPlaying: Boolean(player?.playing),
        };
      }

      const playerStore = usePlayerStore();
      playerStore.resetPlayer();
    },
    activeSource: function (newSource) {
      if ((this.castPending || this.castOwned) && newSource !== this.castLockedSourceMode) {
        this.activeSource = this.castLockedSourceMode;
      }
    },
    activeQuality: function (newQuality) {
      if (this.castPending || this.castOwned) {
        if (newQuality !== this.castLockedQuality) this.activeQuality = this.castLockedQuality;
        return;
      }
      if (this.castReturning) return;
      if (newQuality !== 'auto' && !this.useRawFile) {
        this.activeSource = 'preview';
      }
    },
    getPoster: function (newValue) {
      if (newValue && this.plyr()) {
        this.plyr().poster = newValue;
      }

      return '';
    },
    // Watch the 'getSeekTimeEvent' value change in the PlayerStore state mngr
    getSeekTimeEvent: function (newValue) {
      if (newValue !== null) {
        this.seekPlayback(newValue);

        this.$nextTick(function () {
          const playerStore = usePlayerStore();
          playerStore.setSeekTimeEvent(null);
        });
      }
    },
    getPauseEvent: function (newValue) {
      if (newValue !== null) {
        this.pausePlayback();
        const playerStore = usePlayerStore();
        playerStore.setPauseEvent(null);
      }
    },
    activeSubtitle: function (activeSub) {
      this.refreshAirPlayClock();
      this.enforceAirPlayAccessibility();
      if (this.castOwned || this.castReturning) {
        this.enforceCastAccessibility(activeSub);
        return;
      }
      this.syncLocalSubtitle(activeSub);
    },
    activeAudioDescription: function (activeAudioDescription) {
      this.refreshAirPlayClock();
      if (this.enforceAirPlayAccessibility()) return;
      if (this.castOwned || this.castReturning) {
        this.enforceCastAccessibility(this.activeSubtitle);
        this.pauseAudioDescription();
        return;
      }
      if (activeAudioDescription) {
        const token = this.castLifecycleToken;
        this.$nextTick(() => {
          if (!this.castDisposed && token === this.castLifecycleToken) this.activateAudioDescription();
        });
        return;
      }

      this.deactivateAudioDescription();
    },
  },
  methods: {
    isCurrentVideoEvent: function (event) {
      if (this.castDisposed) return false;
      const video = event?.currentTarget;
      return !video || (video === this.$refs.plyrVideoSource
        && String(video.dataset.playerPodId) === String(this.pod?.id));
    },
    syncLocalSubtitle: function (activeSub) {
      if (!this.plyr()) {
        return;
      }

      let videoEl = this.plyr().media;
      let currentTracks = videoEl.getElementsByTagName('track');
      for (let i = currentTracks.length - 1; i >= 0; i--) {
        videoEl.removeChild(currentTracks[i]);
      }

      if (activeSub) {
        // create new track tag
        let track = document.createElement('track');
        track.setAttribute('kind', 'captions');
        track.setAttribute('src', activeSub.url);
        track.setAttribute('label', activeSub.label);
        track.setAttribute('srclang', activeSub.language);
        track.setAttribute('default', 'true');

        videoEl.appendChild(track);
        track.track.mode = 'hidden';
        this.plyr().captions.language = activeSub.language;
      }
    },
    initializeCast: async function () {
      try {
        const service = await getGoogleCastSession();
        if (this.castDisposed) return;
        this.castService = markRaw(service);
        this.castUnsubscribe = service.subscribe(this.onCastSnapshot);
      } catch {
        if (!this.castDisposed) this.castSnapshot = { sdkState: 'disabled', connected: false };
      }
    },
    invalidateCastContext: function () {
      this.castLifecycleToken += 1;
      this.castPending = false;
      this.castReturning = false;
      this.castStopRequested = false;
      this.castReturnRetryState = null;
      this.castOwnedPodId = null;
      this.castOwnedAssetId = null;
      this.castPinnedPodId = null;
      this.castPinnedSource = null;
      this.castLockedSourceMode = null;
      this.castLockedQuality = null;
      this.castLoadedCaption = null;
      this.castLocalSubtitle = null;
      this.castLocalAudioDescription = null;
      this.castReturnState = null;
      this.castResumeLocal = false;
      this.castErrorKey = null;
      this.castAccessibilityBlocked = false;
      this.castUnsupportedReceiverCaption = false;
      this.pendingPlaybackState = null;
    },
    castReasonKey: function (reason) {
      const keys = {
        airplay_active: 'airPlayActive',
        audio_description: 'audioDescriptionUnsupported',
        unsupported_captions: 'captionsUnsupported',
        caption_validity: 'captionValidity',
        unknown_duration: 'unknownDuration',
        unknown_expiry: 'unknownExpiry',
        access_expired: 'accessExpired',
        insufficient_validity: 'insufficientValidity',
        unsupported_source: 'loadFailed',
        connection_failed: 'connectionFailed',
        media_load_failed: 'loadFailed',
        remote_playback_failed: 'playbackFailed',
        control_failed: 'controlFailed',
        return_local_blocked: 'resumeLocal',
        unknown: 'playbackFailed',
      };
      return keys[reason] ? `routes.pod.googleCast.${keys[reason]}` : null;
    },
    evaluateCastEligibility: function () {
      const player = this.plyr();
      const duration = Number(player?.duration);
      return getCastEligibility({
        source: this.getSource,
        duration: Number.isFinite(duration) && duration > 0 ? duration : this.getSource?.duration,
        currentTime: Number(player?.currentTime) || 0,
        accessExpiresAt: this.accessExpiresAt,
        activeSubtitle: this.activeSubtitle,
        vttAssets: this.castVttAssets,
        hasAudioDescription: Boolean(this.activeAudioDescription?.url),
        airPlayActive: this.airPlayActive,
        nowMs: Date.now(),
      });
    },
    startCasting: async function () {
      if (!this.castService || this.castLocked || this.castSnapshot?.sdkState !== 'ready') return;
      this.castErrorKey = null;
      const eligibility = this.evaluateCastEligibility();
      if (!eligibility.allowed) {
        this.castErrorKey = this.castReasonKey(eligibility.reason);
        return;
      }
      const asset = this.getSource;
      const podId = this.pod?.id;
      const token = ++this.castLifecycleToken;
      const subtitle = this.activeSubtitle;
      const permittedCaption = this.castVttAssets.find((caption) => caption.id === eligibility.captionAssetId);
      const caption = permittedCaption ? { ...permittedCaption, ...subtitle } : null;
      this.castPinnedSource = asset;
      this.castPinnedPodId = podId;
      this.castLockedSourceMode = this.activeSource;
      this.castLockedQuality = this.activeQuality;
      this.castLocalSubtitle = subtitle;
      this.castLocalAudioDescription = this.activeAudioDescription;
      this.castPending = true;
      this.pendingPlaybackState = null;
      try {
        // start invokes requestSession synchronously, before its first await.
        await this.castService.start({
          podId, asset, caption,
          title: this.pod?.name,
          posterUrl: this.getPoster || null,
          viewContext: this.viewContext,
          getLocalState: () => ({
            currentTime: Number(this.plyr()?.currentTime) || 0,
            paused: !this.plyr()?.playing,
            duration: Number(this.plyr()?.duration) || Number(asset.duration),
          }),
          validate: () => {
            if (this.castDisposed || token !== this.castLifecycleToken || this.pod?.id !== podId
              || this.getSource?.id !== asset.id || !this.isCastAssetAuthorized(asset)) return false;
            const current = this.evaluateCastEligibility();
            return current.allowed && current.captionAssetId === eligibility.captionAssetId;
          },
        });
      } finally {
        if (!this.castDisposed && token === this.castLifecycleToken) {
          this.castPending = false;
          this.reconcileCastSession();
          if (!this.castOwned) {
            this.castPinnedSource = null;
            this.castPinnedPodId = null;
            this.castErrorKey = this.castReasonKey(this.castSnapshot?.errorCode);
          }
        }
      }
    },
    isCastAssetAuthorized: function (asset) {
      if (!asset?.url || !asset.mimetype || asset.broken) return false;
      if (!this.useRawFile && this.playableWebAssets.some((candidate) => candidate.id === asset.id && candidate.url === asset.url)) return true;
      if (!this.canActuallyPlayRawFile || !(this.useRawFile || this.allowSourceSelection)) return false;
      return (this.pod?.asset?.raw || []).some((candidate) => (
        candidate.id === asset.id && candidate.url === asset.url && candidate.mimetype
        && !candidate.broken && candidate.state === this.getPodStates.COMPLETE
      ));
    },
    reconcileCastSession: function () {
      const service = this.castService;
      if (this.castDisposed || !service || !this.castSnapshot?.connected
        || !this.castSnapshot.mediaLoaded || this.castReturning || this.airPlayActive) return;
      const candidates = [...this.playableWebAssets, ...(this.pod?.asset?.raw || [])];
      const asset = candidates.find((candidate) => this.isCastAssetAuthorized(candidate)
        && service.matchesMedia({ podId: this.pod?.id, asset: candidate }));
      if (!asset) {
        if (this.castOwned) {
          const subtitle = this.castLocalSubtitle;
          const audioDescription = this.castLocalAudioDescription;
          this.plyr()?.pause();
          this.pauseAudioDescription();
          this.invalidateCastContext();
          const playerStore = usePlayerStore();
          playerStore.setIsPlaying(false);
          playerStore.setActiveSubtitle(subtitle);
          playerStore.setActiveAudioDescription(audioDescription);
        }
        return;
      }
      if (!this.castOwned) {
        if (!this.castPending) {
          this.castLockedSourceMode = this.activeSource;
          this.castLockedQuality = this.activeQuality;
          this.castLocalSubtitle = this.activeSubtitle;
          this.castLocalAudioDescription = this.activeAudioDescription;
        }
        this.castPinnedSource = asset;
        this.castPinnedPodId = this.pod?.id;
        this.castOwnedPodId = this.pod?.id;
        this.castOwnedAssetId = asset.id;
        this.pendingPlaybackState = null;
        this.plyr()?.pause();
        this.pauseAudioDescription();
      }
      this.syncCastPlaybackState();
    },
    onCastSnapshot: function (snapshot) {
      if (this.castDisposed) return;
      const owned = this.castOwned;
      const podId = this.castOwnedPodId;
      const asset = this.castPinnedSource;
      const previousError = this.castSnapshot?.errorCode;
      this.castSnapshot = snapshot;
      if (snapshot.errorCode) this.castErrorKey = this.castReasonKey(snapshot.errorCode);
      else if (previousError && this.castErrorKey === this.castReasonKey(previousError)) this.castErrorKey = null;
      // endSession may clear media before confirming disconnection. Never resume locally yet.
      const matchesOwned = owned && snapshot.mediaLoaded && this.castService.matchesMedia({ podId, asset });
      if (owned && snapshot.connected && (snapshot.explicitReturn
        || (this.castStopRequested && (!snapshot.mediaLoaded || matchesOwned)))) {
        if (matchesOwned) this.syncCastPlaybackState();
        else usePlayerStore().setIsPlaying(false);
        return;
      }
      if (owned && (!snapshot.connected || !snapshot.mediaLoaded
        || !this.castService.matchesMedia({ podId, asset }))) {
        const state = this.castService.getReturnState({ podId, asset });
        this.beginCastReturn(state || this.castReturnState, !snapshot.connected && state?.explicitReturn === true);
        return;
      }
      this.reconcileCastSession();
    },
    syncCastPlaybackState: function () {
      if (!this.castOwned) return;
      const snapshot = this.castSnapshot;
      const playerStore = usePlayerStore();
      playerStore.setCurrentTime(Number(snapshot.currentTime) || 0);
      playerStore.setDuration(Number(snapshot.duration) || Number(this.castPinnedSource?.duration) || 0);
      playerStore.setIsPlaying(!snapshot.paused);
      playerStore.setIsSeeking(false);
      this.castReturnState = {
        currentTime: Number(snapshot.currentTime) || 0,
        duration: Number(snapshot.duration) || 0,
        paused: snapshot.paused !== false,
        explicitReturn: snapshot.explicitReturn === true,
      };
      const captions = this.castService.getCaptionState({ assets: this.castVttAssets });
      this.castLoadedCaption = this.castVttAssets.find((asset) => asset.id === captions.loadedCaptionAssetId) || null;
      this.castUnsupportedReceiverCaption = captions.hasUnsupportedCaption === true;
      this.enforceCastAccessibility(this.activeSubtitle);
      if (!this.castAccessibilityBlocked) {
        const caption = this.castLoadedCaption && captions.activeCaptionAssetId === this.castLoadedCaption.id
          ? playerStore.availableSubtitles.find((item) => item.id === this.castLoadedCaption.id && item.url === this.castLoadedCaption.url)
          : null;
        playerStore.setActiveSubtitle(caption || null);
      }
    },
    enforceCastAccessibility: function (subtitle) {
      if (!this.castOwned) return false;
      const unsupported = this.castUnsupportedReceiverCaption || Boolean(this.activeAudioDescription?.url)
        || Boolean(subtitle?.url && (subtitle.id !== this.castLoadedCaption?.id || subtitle.url !== this.castLoadedCaption?.url));
      this.castAccessibilityBlocked = unsupported;
      if (unsupported) {
        if (subtitle?.url && (subtitle.id !== this.castLoadedCaption?.id || subtitle.url !== this.castLoadedCaption?.url)) {
          this.castLocalSubtitle = subtitle;
        }
        if (this.activeAudioDescription?.url) this.castLocalAudioDescription = this.activeAudioDescription;
        this.castService.pause();
        this.pauseAudioDescription();
        this.castErrorKey = 'routes.pod.googleCast.returnLocalForAccessibility';
      }
      return unsupported;
    },
    changeCastCaption: async function (subtitle) {
      if (!this.castOwned) return;
      if (subtitle?.url && (subtitle.id !== this.castLoadedCaption?.id || subtitle.url !== this.castLoadedCaption?.url)) {
        this.castErrorKey = 'routes.pod.googleCast.returnLocalForAccessibility';
        return;
      }
      const token = this.castLifecycleToken;
      await this.castService.selectCaption(Boolean(subtitle?.url));
      if (token !== this.castLifecycleToken || this.castDisposed || !this.castOwned) return;
      this.syncCastPlaybackState();
    },
    stopCasting: function () {
      if (!this.castService || !this.castSnapshot?.connected) return;
      if (this.castOwned) this.castReturnState = this.castService.getReturnState({
        podId: this.castOwnedPodId, asset: this.castPinnedSource,
      }) || this.castReturnState;
      this.castErrorKey = null;
      if (this.castOwned) this.castStopRequested = true;
      this.castService.stopCasting();
    },
    getAuthorizedCastReturnSource: function (reference) {
      if (!reference?.url) return null;
      const candidates = [...this.playableWebAssets, ...(this.pod?.asset?.raw || [])];
      return candidates.find((asset) => {
        if (asset.id !== reference.id || !this.isCastAssetAuthorized(asset)) return false;
        try {
          const oldUrl = new URL(reference.url);
          const currentUrl = new URL(asset.url);
          return oldUrl.protocol === 'https:' && currentUrl.protocol === 'https:'
            && oldUrl.origin === currentUrl.origin && oldUrl.pathname === currentUrl.pathname;
        } catch {
          return false;
        }
      }) || null;
    },
    restoreCastLocalAccessibility: function () {
      const playerStore = usePlayerStore();
      const subtitle = this.castLocalSubtitle;
      const currentSubtitle = subtitle && playerStore.availableSubtitles.find((item) => (
        subtitle.isTranscript ? item.isTranscript && item.transcriptId === subtitle.transcriptId
          : item.id === subtitle.id && Boolean(item.url)
      ));
      const description = this.castLocalAudioDescription;
      const currentDescription = description && playerStore.availableAudioDescriptions.find((item) => item.id === description.id);
      playerStore.setActiveSubtitle(currentSubtitle || subtitle);
      playerStore.setActiveAudioDescription(currentDescription || description);
    },
    beginCastReturn: function (state, explicitReturn) {
      const previousSource = this.castPinnedSource;
      if (!previousSource || this.castPinnedPodId !== this.pod?.id || this.castDisposed) return;
      const source = this.getAuthorizedCastReturnSource(previousSource);
      this.castReturning = true;
      this.castPending = false;
      this.castStopRequested = false;
      this.castOwnedPodId = null;
      this.castOwnedAssetId = null;
      this.castResumeLocal = false;
      this.castReturnRetryState = null;
      this.plyr()?.pause();
      this.pauseAudioDescription();
      this.pendingPlaybackState = {
        sourceId: previousSource.id,
        sourceUrl: source?.url || previousSource.url,
        currentTime: Math.max(0, Number(state?.currentTime) || 0),
        wasPlaying: explicitReturn && state?.paused === false,
        castReturn: true,
        finishing: false,
        token: this.castLifecycleToken,
      };
      if (!source) {
        this.failCastLocalReturn();
        return;
      }
      // Return using this context's current DTO, never a saved receiver URL or another rendition.
      this.castPinnedSource = source;
      if (this.playableWebAssets.some((asset) => asset.id === source.id)) {
        this.activeSource = 'preview';
        this.activeQuality = source.id;
      } else {
        this.activeSource = 'original';
      }
      this.restoreCastLocalAccessibility();
      usePlayerStore().setIsPlaying(false);
      const token = this.castLifecycleToken;
      this.$nextTick(() => {
        if (this.castDisposed || token !== this.castLifecycleToken || !this.castReturning) return;
        if (!this.plyr() || this.plyr().media?.error) this.failCastLocalReturn();
        else if (this.plyr().media?.readyState >= 2) this.applyPendingPlaybackState();
      });
    },
    isCastReturnSourceLoaded: function (pending, requireCurrentData = true) {
      const media = this.plyr()?.media;
      if (!media || media.readyState < (requireCurrentData ? 2 : 1) || media.error) return false;
      try {
        return new URL(media.currentSrc || media.src).href === new URL(pending.sourceUrl).href;
      } catch {
        return false;
      }
    },
    failCastLocalReturn: function () {
      const pending = this.pendingPlaybackState;
      if (!pending?.castReturn || this.castDisposed || pending.token !== this.castLifecycleToken) return;
      const source = this.getAuthorizedCastReturnSource({ id: pending.sourceId, url: pending.sourceUrl });
      this.plyr()?.pause();
      this.pauseAudioDescription();
      this.castReturnRetryState = source ? { ...pending, sourceUrl: source.url, finishing: false, wasPlaying: true } : null;
      this.pendingPlaybackState = null;
      this.castPinnedSource = null;
      this.castPinnedPodId = null;
      this.castReturning = false;
      this.castLoadedCaption = null;
      this.castAccessibilityBlocked = false;
      this.castUnsupportedReceiverCaption = false;
      this.castResumeLocal = Boolean(source);
      this.castErrorKey = 'routes.pod.googleCast.playbackFailed';
      const playerStore = usePlayerStore();
      playerStore.setIsPlaying(false);
      playerStore.setIsSeeking(false);
      playerStore.setCurrentTime(pending.currentTime);
      this.restoreCastLocalAccessibility();
      if (this.plyr()) this.plyr().speed = this.speed;
      this.applyVideoAudioState();
    },
    playAfterCastReturn: function (pending) {
      this.playPlayback().catch(() => {
        if (pending.castReturn && !this.castDisposed && pending.token === this.castLifecycleToken) {
          this.castResumeLocal = true;
          this.plyr()?.pause();
          this.pauseAudioDescription();
          this.castService.reportReturnLocalBlocked();
          this.castErrorKey = 'routes.pod.googleCast.resumeLocal';
          usePlayerStore().setIsPlaying(false);
        }
      });
    },
    finishCastLocalReturn: function (pending, duration) {
      if (this.castDisposed || pending.token !== this.castLifecycleToken || this.pendingPlaybackState !== pending) return;
      if (this.plyr()?.media?.error
        || !this.getAuthorizedCastReturnSource({ id: pending.sourceId, url: pending.sourceUrl })) {
        this.failCastLocalReturn();
        return;
      }
      // Seeking can temporarily drop readyState to metadata; the matching resource already loaded.
      if (!pending.finishing || this.getSource?.url !== pending.sourceUrl || !this.isCastReturnSourceLoaded(pending, false)) {
        pending.finishing = false;
        return;
      }
      // Keep the pending/return guards through the unpin's source watcher and DOM update.
      this.pendingPlaybackState = null;
      this.castReturning = false;
      this.castReturnRetryState = null;
      this.castLoadedCaption = null;
      this.castAccessibilityBlocked = false;
      this.castUnsupportedReceiverCaption = false;
      const playerStore = usePlayerStore();
      playerStore.setCurrentTime(this.plyr().currentTime);
      playerStore.setDuration(duration || 0);
      playerStore.setIsPlaying(false);
      playerStore.setIsSeeking(false);
      this.restoreCastLocalAccessibility();
      this.plyr().speed = this.speed;
      this.applyVideoAudioState();
      this.syncLocalSubtitle(this.activeSubtitle);
      this.syncAudioDescription(true);
      if (pending.wasPlaying) this.playAfterCastReturn(pending);
    },
    applyPendingPlaybackState: function () {
      const pending = this.pendingPlaybackState;
      const player = this.plyr();
      if (!pending || !player || pending.sourceId !== this.getSource?.id) return false;
      if (pending.castReturn) {
        if (pending.token !== this.castLifecycleToken) return false;
        if (player.media?.error
          || !this.getAuthorizedCastReturnSource({ id: pending.sourceId, url: pending.sourceUrl })) {
          this.failCastLocalReturn();
          return true;
        }
        if (pending.finishing) return true;
        if (!this.isCastReturnSourceLoaded(pending)) return false;
      }
      const nativeDuration = Number(player.duration);
      const duration = Number.isFinite(nativeDuration) && nativeDuration > 0 ? nativeDuration : Number(this.getSource?.duration);
      const maximumTime = Number.isFinite(duration) && duration > 0 ? Math.max(duration - 0.1, 0) : Infinity;
      player.currentTime = Math.min(pending.currentTime, maximumTime);
      if (pending.castReturn) {
        pending.finishing = true;
        this.castPinnedSource = null;
        this.castPinnedPodId = null;
        this.$nextTick(() => this.finishCastLocalReturn(pending, duration));
      } else {
        this.pendingPlaybackState = null;
        this.syncAudioDescription(true);
        if (pending.wasPlaying) this.playAfterCastReturn(pending);
      }
      return true;
    },
    resumeLocalPlayback: async function () {
      if (!this.castResumeLocal || this.castLocked) return;
      const token = this.castLifecycleToken;
      const retry = this.castReturnRetryState;
      if (retry) {
        const source = this.getAuthorizedCastReturnSource({ id: retry.sourceId, url: retry.sourceUrl });
        if (!source) {
          this.castReturnRetryState = null;
          this.castResumeLocal = false;
          this.castErrorKey = 'routes.pod.googleCast.playbackFailed';
          return;
        }
        this.castReturning = true;
        this.castPinnedSource = source;
        this.castPinnedPodId = this.pod?.id;
        this.pendingPlaybackState = { ...retry, sourceUrl: source.url, token, finishing: false, wasPlaying: true };
        this.castResumeLocal = false;
        this.castErrorKey = null;
        this.$nextTick(() => {
          if (this.castDisposed || token !== this.castLifecycleToken || !this.castReturning) return;
          try {
            if (!this.plyr()?.media) this.failCastLocalReturn();
            else this.plyr().media.load();
          } catch {
            this.failCastLocalReturn();
          }
        });
        return;
      }
      try {
        await this.playPlayback();
        if (!this.castDisposed && token === this.castLifecycleToken) {
          this.castResumeLocal = false;
          this.castErrorKey = null;
        }
      } catch {
        if (!this.castDisposed && token === this.castLifecycleToken) this.castResumeLocal = true;
      }
    },
    playPlayback: async function () {
      if (this.castOwned) {
        if (!this.enforceCastAccessibility(this.activeSubtitle)) this.castService.play();
      } else if (!this.castPending && !this.castReturning) {
        if (this.castReturnRetryState) {
          await this.resumeLocalPlayback();
          return;
        }
        await this.plyr()?.play();
      }
    },
    pausePlayback: async function () {
      if (this.castOwned) this.castService.pause();
      else {
        if (this.castReturning && this.pendingPlaybackState?.castReturn) this.pendingPlaybackState.wasPlaying = false;
        this.plyr()?.pause();
        this.pauseAudioDescription();
      }
    },
    togglePlayback: async function () {
      if (this.castOwned) {
        if (!this.enforceCastAccessibility(this.activeSubtitle)) this.castService.togglePlay();
      } else if (!this.castPending && !this.castReturning) {
        if (this.plyr()?.playing) await this.pausePlayback();
        else await this.playPlayback();
      }
    },
    seekPlayback: function (seconds) {
      if (this.castOwned) this.castService.seek(seconds);
      else if (!this.castPending && !this.castReturning && this.castReturnRetryState) {
        if (!Number.isFinite(seconds)) return;
        const duration = Number(usePlayerStore().duration);
        const target = Math.max(0, Number.isFinite(duration) && duration > 0 ? Math.min(seconds, duration) : seconds);
        this.castReturnRetryState.currentTime = target;
        usePlayerStore().setCurrentTime(target);
      } else if (!this.castPending && !this.castReturning && this.plyr()) this.plyr().currentTime = seconds;
    },
    refreshAirPlayClock: function () {
      this.airPlayNowMs = Date.now();
      const shouldRun = (this.airPlaySupported && this.airPlayAvailable) || this.airPlayActive;
      if (shouldRun && this.airPlayClockTimer === null) {
        this.airPlayClockTimer = setInterval(() => { this.airPlayNowMs = Date.now(); }, 30000);
      } else if (!shouldRun && this.airPlayClockTimer !== null) {
        clearInterval(this.airPlayClockTimer);
        this.airPlayClockTimer = null;
      }
    },
    syncAirPlayState: function () {
      const video = this.$refs.plyrVideoSource;
      this.airPlaySupported = typeof video?.webkitShowPlaybackTargetPicker === 'function';
      this.airPlayMediaDuration = video?.duration;
      this.airPlayCurrentTime = video?.currentTime || 0;
      if (video) this.onAirPlayWirelessChanged({ currentTarget: video });
      this.refreshAirPlayClock();
    },
    onAirPlayAvailabilityChanged: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      this.airPlaySupported = typeof event.currentTarget?.webkitShowPlaybackTargetPicker === 'function';
      this.airPlayAvailable = event.availability === 'available';
      this.refreshAirPlayClock();
    },
    onAirPlayWirelessChanged: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      const active = event.currentTarget?.webkitCurrentPlaybackTargetIsWireless === true;
      if (active === this.airPlayActive) return;
      if (active) {
        // Capture before flipping active: getSource consults the pin while wireless.
        this.airPlayPinnedSource = this.getSource;
        this.airPlayPinnedPodId = this.pod?.id;
        this.airPlaySessionId = uuidv4();
        this.airPlayActive = true;
        this.airPlayErrorKey = null;
        this.enforceAirPlayAccessibility();
        this.recordAirPlayEvent('airplay_session_start');
      } else {
        this.recordAirPlayEvent('airplay_session_stop');
        this.airPlayActive = false;
        this.airPlayPinnedSource = null;
        this.airPlayPinnedPodId = null;
        this.airPlaySessionId = null;
        this.airPlayErrorKey = null;
      }
      this.refreshAirPlayClock();
    },
    enforceAirPlayAccessibility: function () {
      if (!this.airPlayActive || (!this.activeAudioDescription?.url && !this.activeSubtitle?.url)) return false;
      this.plyr()?.pause();
      this.pauseAudioDescription();
      this.airPlayErrorKey = this.airPlayReasonKey(this.activeAudioDescription?.url ? 'audio_description' : 'captions');
      return true;
    },
    airPlayReasonKey: function (reason) {
      const keys = {
        audio_description: 'audioDescriptionUnsupported',
        captions: 'captionsUnsupported',
        unknown_duration: 'unknownDuration',
        unknown_expiry: 'unknownExpiry',
        access_expired: 'accessExpired',
        insufficient_validity: 'insufficientValidity',
      };
      return keys[reason] ? `routes.pod.airPlay.${keys[reason]}` : null;
    },
    evaluateAirPlayEligibility: function (nowMs) {
      const nativeDuration = this.airPlayMediaDuration;
      return getAirPlayEligibility({
        url: this.getSource?.url,
        duration: Number.isFinite(nativeDuration) && nativeDuration > 0 ? nativeDuration : this.getSource?.duration,
        currentTime: this.airPlayCurrentTime,
        accessExpiresAt: this.accessExpiresAt,
        hasSubtitle: Boolean(this.activeSubtitle?.url),
        hasAudioDescription: Boolean(this.activeAudioDescription?.url),
        nowMs,
      });
    },
    openAirPlayPicker: function () {
      if (this.castLocked) return;
      const video = this.$refs.plyrVideoSource;
      this.airPlayErrorKey = null;
      this.refreshAirPlayClock();
      this.airPlayMediaDuration = video?.duration;
      this.airPlayCurrentTime = video?.currentTime || 0;
      if (typeof video?.webkitShowPlaybackTargetPicker !== 'function') return;
      if (!this.airPlayActive) {
        const eligibility = this.evaluateAirPlayEligibility(Date.now());
        if (!this.canPlaySource() || !eligibility.allowed) {
          this.airPlayErrorKey = this.airPlayReasonKey(eligibility.reason || 'unknown_expiry');
          return;
        }
        if (!this.airPlayAvailable) return;
      }
      try {
        // Must stay in the synchronous user gesture stack.
        video.webkitShowPlaybackTargetPicker();
      } catch {
        this.airPlayErrorKey = 'routes.pod.airPlay.pickerFailed';
        this.recordAirPlayEvent('airplay_error');
      }
    },
    recordAirPlayEvent: function (eventName, errorCode = 0) {
      if (!import.meta.env.PROD) return;
      try {
        const source = this.airPlayPinnedSource || this.getSource;
        gtagEvent(eventName, {
          playback_session_id: this.airPlaySessionId || uuidv4(),
          pod_id: this.airPlayPinnedPodId ?? this.pod?.id,
          asset_id: source?.id,
          view_context: ['file', 'accesslink', 'embedded'].includes(this.viewContext) ? this.viewContext : 'file',
          error_code: [1, 2, 3, 4].includes(errorCode) ? errorCode : 0,
          page_location: window.location.origin,
          page_referrer: window.location.origin,
          page_title: 'AirPlay',
        });
      } catch {
        // Analytics is best-effort and must never interrupt native playback.
      }
    },
    plyr: function () {
      if (this.$refs && this.$refs.plyr && this.$refs.plyr.player) {
        return this.$refs.plyr.player;
      }
      return null;
    },
    setQuality: function (quality) {
      if (this.airPlayActive || this.castLocked) return;
      this.activeQuality = quality;
    },
    getAssetHeight: function (asset) {
      const resolutionHeight = Number(asset.resolution?.split('x')[1]);
      const profileHeight = Number(asset.name?.match(/-(\d+)p\.mp4$/)?.[1]);
      const height = Number.isSafeInteger(resolutionHeight) && resolutionHeight > 0
        ? resolutionHeight
        : profileHeight;
      return Number.isSafeInteger(height) && height > 0 ? height : null;
    },
    plyReady: function (e) {
      if (!this.isCurrentVideoEvent(e)) return;
      this.syncAirPlayState();

      if (!this.plyr()) {
        // The DOM element of player got deleted before ready
        return;
      }

      this.plyr()
        .on('enterfullscreen', this.plyrEnterFullscreen);
      this.plyr()
        .on('exitfullscreen', this.plyrExitFullScreen);
      if (this.castOwned || this.castReturning) return;
      this.setDuration(e.target.duration || this.getPodDuration);

      // update player settings from the store
      const playerStore = usePlayerStore();
      this.plyr().volume = playerStore.volume;
      this.plyr().muted = playerStore.muted;
      this.plyr().speed = playerStore.speed;
      this.applyVideoAudioState();

    },
    plyrLoadedData: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      this.syncAirPlayState();
      const player = this.plyr();
      if (this.castReturnRetryState && !this.castReturning) return;
      if (this.castOwned || this.castPending) return;
      if (this.applyPendingPlaybackState()) return;
      if (this.castReturning || !player) return;

      const startTime = Number(this.$route.query.start);
      if (!Number.isNaN(startTime)) {
        player.currentTime = startTime / 1000;
      }
    },
    plyrPlay: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      if (this.castOwned || this.castReturning || this.castReturnRetryState) {
        this.plyr()?.pause();
        this.pauseAudioDescription();
        return;
      }
      if (this.enforceAirPlayAccessibility()) return;
      usePlayerStore().setIsPlaying(true);
      this.playAudioDescription();
    },
    plyrPause: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      if (!this.castOwned && !this.castReturning) usePlayerStore().setIsPlaying(false);
      this.pauseAudioDescription();
    },
    plyrTimeUpdate: function (e) {
      if (!this.isCurrentVideoEvent(e)) return;
      if (this.castOwned || this.castReturning || this.castReturnRetryState) return;
      this.airPlayMediaDuration = e.target.duration;
      this.airPlayCurrentTime = e.target.currentTime;
      // set the current time for all components that are interest
      const playerStore = usePlayerStore();
      playerStore.setCurrentTime(e.target.currentTime);
      this.setDuration(e.target.duration || this.getPodDuration);
      this.syncAudioDescription(false);
    },
    plyrVolumeChanged: function (e) {
      if (!this.isCurrentVideoEvent(e)) return;
      if (this.castOwned || this.castReturning || this.castPending) return;
      if (this.activeAudioDescription) {
        this.applyVideoAudioState();
        return;
      }

      const playerStore = usePlayerStore();
      playerStore.setVolume(e.target.volume);
      playerStore.setMuted(e.target.muted);
    },
    plyrEnterFullscreen: function () {
      const playerStore = usePlayerStore();
      playerStore.setFullscreen(true);
    },
    plyrExitFullScreen: function () {
      const playerStore = usePlayerStore();
      playerStore.setFullscreen(false);
    },
    plyrSeeking: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      if (this.castOwned || this.castReturning || this.castReturnRetryState) return;
      const playerStore = usePlayerStore();
      playerStore.setIsSeeking(true);
    },
    plyrSeeked: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      if (this.castOwned || this.castReturning || this.castReturnRetryState) return;
      const playerStore = usePlayerStore();
      playerStore.setIsSeeking(false);
      this.syncAudioDescription(true);
    },
    plyrSpeedChanged: function (e) {
      if (!this.isCurrentVideoEvent(e)) return;
      if (this.castLocked) return;
      const playerStore = usePlayerStore();
      playerStore.setSpeed(e.target.playbackRate);
      this.setAudioDescriptionSpeed(e.target.playbackRate);
    },
    canPlaySource: function () {
      if (!this.castLocked && this.activeSource === 'original' && !this.canActuallyPlayRawFile) {
        return false;
      }

      if (!this.getSource) {
        return false;
      }

      const sourceMimeType = this?.getSource?.mimetype;
      if (sourceMimeType) {
        const videoElement = document.createElement('video');
        return videoElement.canPlayType(sourceMimeType) !== '';
      }

      return false;
    },
    setIsPlaying: function (value) {
      return value ? this.playPlayback() : this.pausePlayback();
    },
    setDuration: function (duration) {
      const playerStore = usePlayerStore();
      playerStore.setDuration(duration);
    },
    refreshAutoQuality: function () {
      if (this.castLocked) return;
      if (this.activeQuality === 'auto') {
        this.$forceUpdate();
      }
    },
    onWaveformToggle: function (showingWaveform) {
      if (this.castLocked) return;
      if (!this.waveFormHasLoaded) {
        this.$nextTick(() => {
          if (this.castDisposed || this.castLocked) return;
          // Well, uhm, useAVWaveForm explicitly expects for the player element to be a Ref object.
          useAVWaveform(toRef(this.$refs.plyrVideoSource), this.$refs.waveformCanvas, {
            src: this.getSource.url,
            playedLineColor: '#3dabff',
            noplayedLineColor: '#3dabff',
            canvWidth: 400,
            canvHeight: 80,
          });
        });

        this.pausePlayback();
        this.waveFormHasLoaded = true;
      }

      this.waveformToggle = showingWaveform;
    },
    keyPressed(e) {
      const target = e.target;
      const interactiveSelector = 'button, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="slider"], input, textarea, select, a, [contenteditable="true"]';

      if (
        target &&
        target !== e.currentTarget &&
        (target.isContentEditable || (typeof target.closest === 'function' && target.closest(interactiveSelector)))
      ) {
        return;
      }

      if (!this.plyr()) {
        return;
      }

      switch (e.key) {
        case 'f':
          this.plyr()
              .fullscreen
              .toggle();
          break;
        case 'p':
          this.togglePlayback().catch(() => {});
          break;
        case ' ':
          this.togglePlayback().catch(() => {});
          break;
        case 'm':
          this.togglePlayerMuted();
          break;
        case 'ArrowUp':
          this.setPlayerVolume(Math.min(1, (this.castOwned ? this.castSnapshot.volume : this.volume) + 0.1));
          break;
        case 'ArrowDown':
          this.setPlayerVolume(Math.max(0, (this.castOwned ? this.castSnapshot.volume : this.volume) - 0.1));
          break;
        case 'ArrowLeft':
          this.seekPlayback((this.castOwned ? this.castSnapshot.currentTime : this.plyr().currentTime) - 1);
          break;
        case 'ArrowRight':
          this.seekPlayback((this.castOwned ? this.castSnapshot.currentTime : this.plyr().currentTime) + 1);
          break;
        case '0':
          this.seekToPercentage(0);
          break;
        case '1':
          this.seekToPercentage(0.1);
          break;
        case '2':
          this.seekToPercentage(0.2);
          break;
        case '3':
          this.seekToPercentage(0.3);
          break;
        case '4':
          this.seekToPercentage(0.4);
          break;
        case '5':
          this.seekToPercentage(0.5);
          break;
        case '6':
          this.seekToPercentage(0.6);
          break;
        case '7':
          this.seekToPercentage(0.7);
          break;
        case '8':
          this.seekToPercentage(0.8);
          break;
        case '9':
          this.seekToPercentage(0.9);
          break;
        default:
          break;
      }

      // propagate only the keys that we don't capture
      const keys = [
        'f', 'p', ' ', 'm', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
      ];
      if (keys.indexOf(e.key) >= 0) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    seekToPercentage(percentage) {
      const duration = this.castOwned ? this.castSnapshot.duration : this.plyr()?.duration;
      if (!duration || duration === 0) {
        return;
      }

      this.seekPlayback(percentage * duration);
    },
    handlePlayerError: function (event) {
      if (!this.isCurrentVideoEvent(event)) return;
      if (this.castReturning) {
        this.failCastLocalReturn();
        return;
      }
      if (this.castReturnRetryState) {
        this.castErrorKey = 'routes.pod.googleCast.playbackFailed';
        return;
      }
      if (this.castLocked) return;
      const video = event?.currentTarget || this.$refs.plyrVideoSource;
      if (video?.webkitCurrentPlaybackTargetIsWireless === true) {
        this.onAirPlayWirelessChanged({ currentTarget: video });
        this.airPlayErrorKey = 'routes.pod.airPlay.playbackFailed';
        this.recordAirPlayEvent('airplay_error', video.error?.code);
        return;
      }
      if (this.activeSource === 'original') {
        this.canActuallyPlayRawFile = false;
      }
    },
    audioDescription: function () {
      return this.$refs.audioDescriptionSource || null;
    },
    activateAudioDescription: function () {
      if (this.castOwned || this.castReturning) {
        this.pauseAudioDescription();
        return;
      }
      this.applyVideoAudioState();
      this.syncAudioDescription(true);
      this.setAudioDescriptionSpeed(this.speed);

      if (this.plyr() && this.plyr().playing) {
        this.playAudioDescription();
      }
    },
    deactivateAudioDescription: function () {
      this.pauseAudioDescription();
      this.applyVideoAudioState();
    },
    applyVideoAudioState: function () {
      if (!this.plyr()) {
        return;
      }

      const media = this.plyr().media;

      if (this.activeAudioDescription) {
        media.muted = true;
        media.volume = this.volume;
        this.applyAudioDescriptionVolume();
        return;
      }

      media.muted = this.muted;
      media.volume = this.volume;
    },
    applyAudioDescriptionVolume: function () {
      const audioDescription = this.audioDescription();

      if (!audioDescription) {
        return;
      }

      audioDescription.volume = this.volume;
      audioDescription.muted = this.muted;
    },
    playAudioDescription: function () {
      if (this.castOwned || this.castReturning) return;
      const audioDescription = this.audioDescription();

      if (!audioDescription || !this.activeAudioDescription) {
        return;
      }

      this.syncAudioDescription(true);
      audioDescription.play().catch(() => {});
    },
    pauseAudioDescription: function () {
      const audioDescription = this.audioDescription();

      if (audioDescription) {
        audioDescription.pause();
      }
    },
    syncAudioDescription: function (force) {
      const audioDescription = this.audioDescription();

      if (!audioDescription || !this.plyr() || !this.activeAudioDescription) {
        return;
      }

      const currentTime = this.plyr().currentTime || 0;
      const drift = Math.abs((audioDescription.currentTime || 0) - currentTime);

      if (force || drift > 0.35) {
        audioDescription.currentTime = currentTime;
      }
    },
    setAudioDescriptionSpeed: function (speed) {
      const audioDescription = this.audioDescription();

      if (audioDescription) {
        audioDescription.playbackRate = speed;
      }
    },
    setPlayerVolume: function (volume) {
      if (this.castOwned) {
        this.castService.setVolume(volume);
        return;
      }
      if (this.castReturning) return;
      const playerStore = usePlayerStore();
      playerStore.setVolume(volume);

      if (this.activeAudioDescription) {
        this.applyAudioDescriptionVolume();
        this.applyVideoAudioState();
        return;
      }

      if (this.plyr()) {
        this.plyr().volume = volume;
      }
    },
    togglePlayerMuted: function () {
      this.setPlayerMuted(!(this.castOwned ? this.castSnapshot.muted : this.muted));
    },
    setPlayerMuted: function (muted) {
      if (this.castOwned) {
        this.castService.setMuted(muted);
        return;
      }
      if (this.castReturning) return;
      const playerStore = usePlayerStore();
      playerStore.setMuted(muted);

      if (this.activeAudioDescription) {
        this.applyAudioDescriptionVolume();
        this.applyVideoAudioState();
        return;
      }

      if (this.plyr()) {
        this.plyr().muted = muted;
      }
    }
  },
  computed: {
    castOwned: function () {
      return this.castOwnedPodId === this.pod?.id && this.castOwnedAssetId !== null;
    },
    castLocked: function () {
      return this.castPending || this.castOwned || this.castReturning;
    },
    playbackCommands: function () {
      return {
        play: this.playPlayback,
        pause: this.pausePlayback,
        togglePlay: this.togglePlayback,
        seek: this.seekPlayback,
      };
    },
    castVttAssets: function () {
      const playerStore = usePlayerStore();
      const commonStore = useCommonStore();
      return (this.pod?.asset?.vtt || []).filter((asset) => (
        asset.state === commonStore.assetStates.COMPLETE && !asset.broken && asset.url
        && playerStore.availableSubtitles.some((caption) => caption.id === asset.id && caption.url === asset.url)
      ));
    },
    castUi: function () {
      const snapshot = this.castSnapshot;
      const policy = this.evaluateCastEligibility();
      const captions = this.castService?.getCaptionState({ assets: this.castVttAssets });
      return {
        visible: Boolean(snapshot?.connected || (snapshot?.sdkState === 'ready'
          && ['NOT_CONNECTED', 'CONNECTING', 'CONNECTED'].includes(snapshot.castState))),
        active: this.castOwned,
        connected: snapshot?.connected === true,
        busy: this.castPending || this.castReturning
          || (this.castOwned && snapshot?.connected === true && snapshot?.explicitReturn === true),
        disabled: !this.castOwned && (this.castLocked || snapshot?.loading === true
          || snapshot?.sdkState !== 'ready' || !policy.allowed),
        disabledReasonKey: this.castReasonKey(policy.reason),
        errorKey: this.castErrorKey,
        descriptionId: this.castDescriptionId,
        resumeLocal: this.castResumeLocal,
        canPause: this.castOwned ? snapshot?.canPause === true : !this.castReturning && !this.castPending,
        canSeek: this.castOwned ? snapshot?.canSeek === true : !this.castReturning && !this.castPending,
        canControlVolume: this.castOwned ? snapshot?.canControlVolume === true : !this.castReturning,
        volume: snapshot?.volume,
        muted: snapshot?.muted,
        captionAssetId: captions?.activeCaptionAssetId ?? null,
        loadedCaptionAssetId: captions?.loadedCaptionAssetId ?? null,
        hasUnsupportedCaption: captions?.hasUnsupportedCaption === true,
      };
    },
    airPlayEligibility: function () {
      return this.evaluateAirPlayEligibility(this.airPlayNowMs);
    },
    airPlayVisible: function () {
      return this.airPlaySupported && (this.airPlayAvailable || this.airPlayActive);
    },
    airPlayDisabled: function () {
      return this.castLocked || (!this.airPlayActive && (!this.canPlaySource() || !this.airPlayEligibility.allowed));
    },
    airPlayDisabledReason: function () {
      if (this.castLocked) return this.$t('routes.pod.googleCast.localFeaturesUnavailable');
      const key = this.airPlayReasonKey(this.airPlayEligibility.reason);
      return key ? this.$t(key) : null;
    },
    audioDescriptionAssets: function () {
      return this.pod?.asset?.audiodescription || [];
    },
    getPodStates: function () {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    getSeekTimeEvent: function () {
      const playerStore = usePlayerStore();
      return playerStore.getSeekTimeEvent;
    },
    getPauseEvent: function () {
      const playerStore = usePlayerStore();
      return playerStore.getPauseEvent;
    },
    fullscreen: function () {
      const playerStore = usePlayerStore();
      return playerStore.fullscreen;
    },
    activeSubtitle: function () {
      const playerStore = usePlayerStore();
      return playerStore.getActiveSubtitle;
    },
    activeAudioDescription: function () {
      const playerStore = usePlayerStore();
      return playerStore.activeAudioDescription;
    },
    volume: function () {
      const playerStore = usePlayerStore();
      return playerStore.volume;
    },
    muted: function () {
      const playerStore = usePlayerStore();
      return playerStore.muted;
    },
    speed: function () {
      const playerStore = usePlayerStore();
      return playerStore.speed;
    },
    getActiveLayer: function () {
      const playerStore = usePlayerStore();
      return playerStore.getActiveLayer;
    },
    playableWebAssets: function () {
      if (!Array.isArray(this.pod?.asset?.web)) {
        return [];
      }

      return this.pod.asset.web.filter((asset) => (
        asset.state === this.getPodStates.COMPLETE && !asset.broken && asset.url && asset.mimetype
      ));
    },
    webCandidates: function () {
      return this.playableWebAssets
        .map((asset) => ({ asset, height: this.getAssetHeight(asset) }))
        .filter(({ height }) => height !== null)
        .sort((left, right) => left.height - right.height);
    },
    autoSelectedWebAsset: function () {
      if (this.webCandidates.length === 0) {
        return this.playableWebAssets[0] || null;
      }

      const video = this.$refs.plyrVideoSource;
      const displayedHeight = video?.clientHeight || (video?.clientWidth ? video.clientWidth * 9 / 16 : 720);
      const desiredHeight = displayedHeight * (window.devicePixelRatio || 1);
      const maximumHeightByNetwork = {
        'slow-2g': 480,
        '2g': 480,
        '3g': 720,
      }[this.networkInformation?.effectiveType] || Infinity;
      const candidates = this.webCandidates.filter(({ height }) => height <= maximumHeightByNetwork);
      const networkCandidates = candidates.length > 0 ? candidates : [this.webCandidates[0]];

      return (networkCandidates.find(({ height }) => height >= desiredHeight)
        || networkCandidates[networkCandidates.length - 1]).asset;
    },
    selectedWebAsset: function () {
      if (this.activeQuality !== 'auto') {
        const manuallySelected = this.playableWebAssets.find(({ id }) => id === this.activeQuality);
        if (manuallySelected) {
          return manuallySelected;
        }
      }

      return this.autoSelectedWebAsset;
    },
    transcodedAsset: function () {
      return this.selectedWebAsset || {};
    },
    getPoster: function () {
      if (this.pod &&
          this.pod.asset &&
          this.pod.asset.thumbnail &&
          this.pod.asset.thumbnail[0] &&
          this.pod.asset.thumbnail[0].url) {
        return this.pod.asset.thumbnail[0].url;
      }

      return '';
    },
    getSource: function () {
      if (this.castPinnedSource && this.castPinnedPodId === this.pod?.id && this.castLocked) {
        return this.castPinnedSource;
      }
      if (this.airPlayActive && this.airPlayPinnedPodId === this.pod?.id) {
        return this.airPlayPinnedSource;
      }
      if (this.activeSource === null) {
        return null;
      }

      if (!this.useRawFile && this.activeSource !== 'original') {
        return this.selectedWebAsset;
      }

      if (!Array.isArray(this.pod?.asset?.raw)) {
        return null;
      }

      return this.pod.asset.raw.find((asset) => asset.state === this.getPodStates.COMPLETE) || null;
    },
    hasPreviewFile: function () {
      return this.playableWebAssets.length > 0;
    },
    getPodDuration: function () {
      return this.selectedWebAsset?.duration || 0;
    },
    showQualitySelection: function () {
      return this.allowQualitySelection && !this.useRawFile && this.playableWebAssets.length > 1;
    },
    qualityOptions: function () {
      return [
        { text: this.$t('routes.pod.autoQuality'), value: 'auto' },
        ...this.playableWebAssets
          .map((asset) => ({ asset, height: this.getAssetHeight(asset) }))
          .sort((left, right) => (left.height ?? Infinity) - (right.height ?? Infinity))
          .map(({ asset, height }) => ({
            text: height ? `${height}p` : asset.name,
            value: asset.id,
          })),
      ];
    },
    options: function () {
      // Safari prior to v16.4 doesn't support non-prefixed version of the API.
      // After that it is supported on iPad devices, but not on iPhone unless it
      // was enabled as feature flag in browser settings...thanks Apple for being
      // late to the party and act like assholes.
      const divElement = document.createElement('div');
      const canUseFullscreenApi = typeof divElement.requestFullscreen === 'function' ||
          typeof divElement.webkitRequestFullscreen === 'function';

      return {
        controls: '<div id=\'defaultControls\' class=\'plyr__controls\'></div>',
        captions: {
          active: true,
          language: 'auto',
          update: true
        },
        fullscreen: {
          enabled: true,
          fallback: true,
          iosNative: !canUseFullscreenApi,
        },
        storage: this.persistSettings === true,
      };
    },
    sourceOptions: function() {
      return [
        { text: this.$t('routes.pod.previewFile'), value: 'preview' },
        { text: this.$t('routes.pod.originalFile'), value: 'original'},
      ];
    },
    getVideoTagStyle: function () {
      return {
        maxHeight: !this.fullscreen ? '70vh' : 'unset',
      };
    },
    hasWaveform: function () {
      // Get first audio asset with status COMPLETE
      if (this.pod && this.pod.asset && this.pod.asset.audio) {
        for (let i = 0; i < this.pod.asset.audio.length; i++) {
          if (this.pod.asset.audio[i].state === this.getPodStates.COMPLETE) {
            return true;
          }
        }
      }

      return false;
    }
  }
};
</script>

<style>
.plyr {
  font-family: unset !important;
}

.plyr__controls {
  text-align: unset !important;
}

.plyr__poster {
  background-color: rgb(var(--v-theme-tw-surface)) !important;
  background-size: cover !important;
}

.plyr-player-container {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  padding: 20px;
  /* max-height: 420px;
  margin-bottom: 80px; */

  background-color: rgb(var(--v-theme-tw-surface));
}

.plyr-player-container:focus,
.plyr-player-container:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 4px;
}

.plyr__video-wrapper {
  display: flex;
  justify-content: center;

}

/*  .plyr-pod-controls {
     background-color: #2e2e2e;
    background-color: rgba(71, 71, 71, 0.6);
    width: 100%;
  }
*/
</style>
